import { FlagCategory, ProviderAvailabilityStatus } from '@bariendo/lib/constants';
import { getReferenceString, MedplumClient } from '@medplum/core';
import { Flag, Practitioner, Reference } from '@medplum/fhirtypes';
import { fetchBot } from './services';

/**
 * Map ProviderAvailabilityStatus to display labels for UI
 */
export const AVAILABILITY_STATUS_LABELS: Record<ProviderAvailabilityStatus, string> = {
  [ProviderAvailabilityStatus.ONLINE]: 'Available',
  [ProviderAvailabilityStatus.DO_NOT_DISTURB]: 'Do not disturb',
};

/**
 * Map ProviderAvailabilityStatus to color codes for UI
 */
export const AVAILABILITY_STATUS_COLORS: Record<ProviderAvailabilityStatus, string> = {
  [ProviderAvailabilityStatus.ONLINE]: 'green.4',
  [ProviderAvailabilityStatus.DO_NOT_DISTURB]: 'red.6',
};

/**
 * Get the current availability status for a provider
 * Returns the ProviderAvailabilityStatus, or ONLINE (default) if no Flag exists
 * This is read-only from the frontend, which is acceptable
 */
export async function getProviderAvailabilityStatus(
  medplum: MedplumClient,
  provider: Reference<Practitioner>
): Promise<ProviderAvailabilityStatus> {
  const flag = await medplum.searchOne('Flag', {
    subject: getReferenceString(provider),
    category: FlagCategory.PROVIDER_REAL_TIME_AVAILABILITY,
    status: 'active',
  });

  if (!flag) {
    await setProviderAvailabilityStatus(medplum, ProviderAvailabilityStatus.ONLINE);
    return ProviderAvailabilityStatus.ONLINE;
  }

  const statusCode = flag.code?.coding?.[0]?.code;

  // Return the status code if it matches a valid ProviderAvailabilityStatus
  if (statusCode === ProviderAvailabilityStatus.ONLINE || statusCode === ProviderAvailabilityStatus.DO_NOT_DISTURB) {
    return statusCode as ProviderAvailabilityStatus;
  }

  // Default to online if code doesn't match
  return ProviderAvailabilityStatus.ONLINE;
}

/**
 * Set provider availability status by calling the backend bot
 * This ensures all Flag resource creation/updates happen server-side
 */
export async function setProviderAvailabilityStatus(
  medplum: MedplumClient,
  status: ProviderAvailabilityStatus
): Promise<Flag> {
  return await fetchBot<Flag>(medplum, 'set-provider-availability-status', {
    status,
  });
}
